<template>
  <div class="customer-orders-modal">

    <div class="modal-header">
      <h1 class="headline" v-text="$t('customer-orders-modal-headline')"></h1>
      <h2 class="subheadline" v-text="$t('customer-orders-modal-subheadline')"></h2>
    </div>

    <div class="oido-modal-content has-padding">

      <div class="loading" v-if="loading" v-text="$t('customer-orders-modal-loading')"></div>

      <div class="empty" v-if="!loading && orders.length == 0" v-text="$t('customer-orders-modal-empty')"></div>

      <ul class="orders-list" v-if="!loading && orders.length > 0">
        <li class="order" v-for="order of orders" :key="order.id">
          <div class="order-header">
            <div class="order-date" v-text="formatDate(order.dateOrdered)"></div>
            <div class="order-total" v-if="order.total != null">
              {{ Number.parseFloat(order.total).toFixed(2) }}<span class="currency-symbol" v-text="currencySymbol"></span>
            </div>
          </div>

          <ul class="order-items">
            <li class="order-item" v-for="item of order.orderItems" :key="item.id">
              <span class="quantity" v-text="(item.quantity || 1) + '×'"></span>
              <span class="name" v-text="services.translations.translation2Text(item.name)"></span>
              <span class="missing" v-if="!isProductAvailable(item.productId)"
                v-text="$t('customer-orders-modal-product-unavailable')"></span>
            </li>
          </ul>

          <div class="invoices" v-if="visibleInvoices(order).length > 0">
            <div class="invoice" v-for="invoice of visibleInvoices(order)" :key="invoice.id">
              <span class="invoice-label">
                {{ $t(invoice.isCorrection ? 'customer-orders-modal-correction-invoice-label' : 'customer-orders-modal-invoice-label') }}<span v-if="invoice.invoiceNumber"> #{{ invoice.invoiceNumber }}</span>
              </span>
              <OidoDownloadButton class="invoice-download-button"
                :url="invoiceUrl(invoice)"
                :filename="'oido-' + (invoice.invoiceNumber || invoice.id) + '.pdf'"
                :label="$t('customer-orders-modal-download-invoice-button-label')"
                buttonStyle="outline"></OidoDownloadButton>
            </div>
          </div>

          <div class="invoice-pending" v-if="order.invoices && order.invoices.length == 0">
            <span class="invoice-pending-label" v-text="$t('customer-orders-modal-invoice-pending-label')"></span>
            <a class="invoice-refresh-link" href="#" @click.prevent="refreshOrder(order)"
              v-if="refreshingOrderId != order.id"
              v-text="$t('customer-orders-modal-invoice-refresh-label')"></a>
            <span class="invoice-refreshing" v-if="refreshingOrderId == order.id"
              v-text="$t('customer-orders-modal-invoice-refreshing-label')"></span>
          </div>

          <div class="order-actions">
            <oido-button class="repeat-button" @click="repeat(order)"
              :primary="true" buttonStyle="solid"
              :show-spinner="processingOrderId == order.id"
              :label="$t('customer-orders-modal-repeat-button-label')" icon="arrow-forward"></oido-button>
          </div>
        </li>
      </ul>

    </div>

    <div class="buttons">
      <ion-button class="close-button negative" color="dark" fill="clear" @click="cancel"
        v-text="$t('cancel-button-label')"></ion-button>
    </div>
  </div>
</template>

<script setup>
import OidoButton from '../components/OidoButton.vue';
import OidoDownloadButton from '../components/OidoDownloadButton.vue';
import {
  IonButton,
  modalController,
  toastController,
} from "@ionic/vue";

import customerService from '../composables/customerService';
import cartService from '../composables/cartService';
import companyService from '../composables/companyService';
import config from '../../config';
import dayjs from 'dayjs';
</script>
<script>
export default {
  props: [],
  data() {
    return {
      orders: [],
      loading: true,
      processingOrderId: null,
      refreshingOrderId: null,
    }
  },
  computed: {
  },
  async mounted() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      let companyId = companyService.company && companyService.company.value ? companyService.company.value.id : null
      let result = await customerService.fetchOrders(companyId)
      this.orders = (result && Array.isArray(result.orders)) ? result.orders : []
      this.loading = false
    },
    formatDate(date) {
      if (!date) return ''
      return dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    isProductAvailable(productId) {
      return Boolean(companyService.getProductById(productId))
    },
    /**
     * Hide invoices that have been superseded by a correction invoice so the customer
     * only sees the latest authoritative documents to download.
     */
    visibleInvoices(order) {
      let invoices = (order && Array.isArray(order.invoices)) ? order.invoices : []
      if (invoices.length == 0) return []
      let supersededIds = new Set()
      for (let invoice of invoices) {
        if (invoice.isCorrection && invoice.correctedInvoiceId) {
          supersededIds.add(invoice.correctedInvoiceId)
        }
      }
      return invoices.filter((invoice) => !supersededIds.has(invoice.id))
    },
    invoiceUrl(invoice) {
      return config.apiUrl + '/api/pdf/invoice/' + invoice.id + '.pdf'
    },
    async refreshOrder(order) {
      if (this.refreshingOrderId) return
      this.refreshingOrderId = order.id
      let companyId = companyService.company && companyService.company.value ? companyService.company.value.id : null
      let result = await customerService.fetchOrders(companyId)
      let freshOrders = (result && Array.isArray(result.orders)) ? result.orders : []
      let fresh = freshOrders.find((o) => o.id == order.id)
      if (fresh) {
        let index = this.orders.findIndex((o) => o.id == order.id)
        if (index >= 0) {
          this.orders.splice(index, 1, fresh)
        }
      }
      this.refreshingOrderId = null
    },
    cancel() {
      modalController.dismiss(false);
    },
    async repeat(order) {
      if (this.processingOrderId) return
      this.processingOrderId = order.id

      let result = cartService.repeatOrder(order)

      this.processingOrderId = null

      let messageKey = 'customer-orders-modal-repeat-success'
      if (result.added == 0) {
        messageKey = 'customer-orders-modal-repeat-empty'
      } else if (result.skipped > 0) {
        messageKey = 'customer-orders-modal-repeat-partial'
      }

      const toast = await toastController.create({
        message: this.$t(messageKey),
        duration: 2500,
        position: 'bottom',
      })
      await toast.present()

      if (result.added > 0) {
        modalController.dismiss(true)
      }
    },
  },
}
</script>
<style scoped>
.customer-orders-modal {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: white;
}

.customer-orders-modal .oido-modal-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  overflow: hidden;
  overflow-y: auto;
  padding: 1rem 2rem;
}

.customer-orders-modal .modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem 2rem 0rem;
  text-align: center;
}

.customer-orders-modal .modal-header .headline {
  margin-top: 0;
  font-size: 1.2rem;
  margin-bottom: 0.3rem;
}

.customer-orders-modal .modal-header .subheadline {
  margin-top: 0;
  font-size: 1rem;
  color: #666;
}

.loading,
.empty {
  text-align: center;
  color: #666;
  padding: 1rem 0;
}

.orders-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.order {
  border: 1px solid #eee;
  border-radius: 0.4rem;
  padding: 0.8rem 1rem;
  margin-bottom: 0.8rem;
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.order-date {
  font-size: 0.85rem;
  color: #555;
}

.order-total {
  font-weight: 600;
}

.currency-symbol {
  margin-left: 0.15rem;
}

.order-items {
  list-style: none;
  padding: 0;
  margin: 0 0 0.6rem 0;
  font-size: 0.85rem;
}

.order-item {
  padding: 0.1rem 0;
}

.order-item .quantity {
  color: #888;
  margin-right: 0.3rem;
}

.order-item .missing {
  margin-left: 0.4rem;
  font-size: 0.75rem;
  color: #b00;
}

.invoices {
  margin: 0 0 0.6rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.invoice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  gap: 0.5rem;
}

.invoice-label {
  color: #444;
}

.invoice-download-button {
  padding: 0.2rem 0.6rem;
}

.invoice-pending {
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invoice-refresh-link {
  color: var(--ion-color-primary, #3880ff);
  text-decoration: underline;
  cursor: pointer;
}

.invoice-refreshing {
  font-style: italic;
}

.order-actions {
  display: flex;
  justify-content: flex-end;
}

.customer-orders-modal .buttons {
  display: flex;
  align-items: center;
  justify-content: end;
  width: 100%;
  padding: 1rem 2rem 1.5rem;
}

.customer-orders-modal .oido-button,
.customer-orders-modal .buttons ion-button {
  margin: 0 0.5rem;
}

.order-actions{padding-top: 1rem;}
</style>
